/**
 * Système de comparaison de produits
 * Gère la sélection, le stockage et l'affichage des produits à comparer
 */

/**
 * Fonction globale pour nettoyer les titres des produits
 * @param {string} title - Le titre original du produit
 * @returns {string} - Le titre nettoyé
 */
function cleanProductTitleGlobal(title) {
	// Récupérer les settings globaux depuis window.themeSettings
	let textToRemove = '';

	if (window.themeSettings && window.themeSettings.remove_text_from_titles) {
		textToRemove = window.themeSettings.remove_text_from_titles;
		console.log('textremove', textToRemove)
	} else {
		// Fallback : essayer de récupérer depuis les options du comparateur
		const scriptTag = document.querySelector('script[data-comparator-options]');
		if (scriptTag) {
			try {
				const options = JSON.parse(scriptTag.textContent);
				textToRemove = options.removeTextFromTitles || '';
			} catch (e) {
				console.warn('Impossible de parser les options du comparateur');
			}
		}
	}

	if (!textToRemove || textToRemove.trim() === '') {
		return title;
	}

	// Séparer les différents textes à supprimer (séparés par des virgules)
	const textsToRemove = textToRemove
		.split(',')
		.map((text) => text.trim())
		.filter((text) => text);

	let cleanedTitle = title;

	// Supprimer chaque texte de façon insensible à la casse
	textsToRemove.forEach((text) => {
		const regex = new RegExp(text.replace(/[.*+?^${}()|[\]\\]/g, '\\$&'), 'gi');
		cleanedTitle = cleanedTitle.replace(regex, '').trim();
	});

	// Nettoyer les espaces multiples et les tirets en début/fin
	cleanedTitle = cleanedTitle.replace(/\s+/g, ' ').replace(/^[-\s]+|[-\s]+$/g, '');

	return cleanedTitle || title; // Retourner le titre original si le nettoyage donne une chaîne vide
}

class ProductComparison {
	constructor() {
		this.maxProducts = 4;
		this.storageKey = 'eps_comparison_products';
		this.isComparatorPage = this.isOnComparatorPage();

		// Toujours charger depuis localStorage pour maintenir la synchronisation
		this.products = this.loadFromStorage();

		this.comparisonBar = null;
		this.mobileComparisonBar = null;

		this.init();
	}

	/**
	 * Vérifie si on est sur la page comparateur
	 */
	isOnComparatorPage() {
		return (
			window.location.pathname.includes('page-comparator') ||
			(window.location.pathname.includes('/pages/') &&
				(document.body.classList.contains('template-page') ||
					window.location.pathname.includes('comparator')))
		);
	}

	/**
	 * Détecte si on est sur mobile
	 */
	isMobile() {
		return window.innerWidth <= 768;
	}

	/**
	 * Initialise le système de comparaison
	 */
	init() {
		if (this.isOnComparatorPage()) {
			return;
		}

		this.createComparisonBars();
		this.bindEvents();
		this.loadFromStorage();
		this.initSlider(); // Initialiser le slider
		this.restoreBarState(); // Restaurer l'état de la barre
		this.updateDisplay(false);
		this.updateCheckboxes();

		// Ajuster la position du sélecteur de langue au chargement
		this.adjustLanguageSelector(this.products.length > 0);
	}

	/**
	 * Initialise les références aux barres de comparaison existantes
	 */
	createComparisonBars() {
		// Ne pas utiliser les barres sur la page du comparateur
		if (this.isComparatorPage) {
			this.hideComparisonBars();
			return;
		}

		// Récupérer les références aux éléments existants dans le DOM
		this.comparisonBar = document.querySelector('[data-comparison-bar-desktop]');
		this.mobileComparisonBar = document.querySelector('[data-comparison-bar-mobile]');

		if (!this.comparisonBar || !this.mobileComparisonBar) {
			console.warn('⚠️ Barres de comparaison non trouvées dans le DOM');
			return;
		}
	}

	/**
	 * Masque les barres de comparaison sur la page comparateur
	 */
	hideComparisonBars() {
		const desktopBar = document.querySelector('[data-comparison-bar-desktop]');
		const mobileBar = document.querySelector('[data-comparison-bar-mobile]');

		if (desktopBar) desktopBar.style.display = 'none';
		if (mobileBar) mobileBar.style.display = 'none';
	}

	/**
	 * Lie les événements
	 */
	bindEvents() {
		// Événements des checkboxes de comparaison
		document.addEventListener('change', async (e) => {
			if (e.target.classList.contains('product-compare-checkbox')) {
				await this.handleCheckboxChange(e.target);
			}
		});

		// Événements des barres de comparaison
		document.addEventListener('click', (e) => {
			// Utiliser les data attributes pour une meilleure sélection
			if (e.target.closest('[data-comparison-clear]')) {
				this.clearAll();
			}

			if (e.target.closest('[data-comparison-compare]')) {
				this.goToComparison();
			}

			// Gestion du bouton de suppression
			const removeButton = e.target.closest('[data-comparison-remove]');
			if (removeButton) {
				e.preventDefault();
				e.stopPropagation();
				const productId = removeButton.dataset.productId;
				this.removeProduct(productId);
			}

			// Toggle de la barre desktop
			if (e.target.closest('[data-comparison-trigger-desktop]')) {
				this.toggleDesktopBar();
			}

			// Toggle de la barre mobile
			if (e.target.closest('[data-comparison-trigger-mobile]')) {
				this.toggleMobileBar();
			}

			// Boutons de navigation du slider
			if (e.target.closest('[data-comparison-scroll-prev]')) {
				this.scrollSlider('prev');
			}

			if (e.target.closest('[data-comparison-scroll-next]')) {
				this.scrollSlider('next');
			}

			if (e.target.closest('[data-comparison-scroll-prev-mobile]')) {
				this.scrollSlider('prev', true);
			}

			if (e.target.closest('[data-comparison-scroll-next-mobile]')) {
				this.scrollSlider('next', true);
			}
		});

		// Recalculer le slider lors du redimensionnement
		window.addEventListener('resize', () => {
			if (this.products.length > 0) {
				setTimeout(() => {
					this.resetSlider();
				}, 100);
			}
		});
	}

	/**
	 * Gère le changement d'état d'une checkbox
	 */
	async handleCheckboxChange(checkbox) {
		// Vérifier si la checkbox a les données du produit
		if (!checkbox.dataset.productData) {
			console.error('❌ ERREUR: checkbox.dataset.productData est manquant!');
			return;
		}

		let productData;
		try {
			productData = JSON.parse(checkbox.dataset.productData);
		} catch (e) {
			console.error('❌ ERREUR lors du parsing des données du produit:', e);
			return;
		}

		if (checkbox.checked) {
			await this.addProduct(productData);
		} else {
			this.removeProduct(productData.id);
		}

		console.log(
			'📊 localStorage après action:',
			localStorage.getItem('eps_comparison_products')
				? JSON.parse(localStorage.getItem('eps_comparison_products')).length + ' produits'
				: 'vide',
		);
	}

	/**
	 * Ajoute un produit à la comparaison
	 */
	async addProduct(productData) {
		// Vérifier la limite
		if (this.products.length >= this.maxProducts) {
			console.warn(`❌ Limite atteinte: ${this.products.length}/${this.maxProducts} produits`);
			const title = window.languages?.popup_max_products_title || 'Limite atteinte';
			const message =
				window.languages?.popup_max_products_message?.replace('{{ max }}', this.maxProducts) ||
				`Vous ne pouvez comparer que ${this.maxProducts} produits maximum. Supprimez un produit pour en ajouter un autre.`;

			if (window.ComparisonPopup) {
				window.ComparisonPopup.show(title, message);
			} else {
				alert(message);
			}
			this.updateCheckboxes(); // Reset les checkboxes
			return false;
		}

		// Vérifier d'abord si le produit a un schéma
		const productSchema = await this.getProductSchema(productData.id);

		if (!productSchema) {
			console.warn(`❌ Le produit ${productData.id} n'a pas de données de comparaison`);
			const title = window.languages?.popup_no_schema_title || 'Produit non comparable';
			const message =
				window.languages?.popup_no_schema_message ||
				"Ce produit n'a pas de données de comparaison et ne peut pas être ajouté à la comparaison.";

			if (window.ComparisonPopup) {
				window.ComparisonPopup.show(title, message);
			} else {
				alert(message);
			}
			this.updateCheckboxes(); // Reset les checkboxes
			return false;
		}

		// Vérifier la compatibilité des schémas si on a déjà des produits
		if (this.products.length > 0) {
			const isCompatible = await this.checkSchemaCompatibility(productData);
			if (!isCompatible) {
				this.updateCheckboxes(); // Reset les checkboxes
				return false;
			}
		}

		// Vérifier si le produit n'est pas déjà ajouté
		const existingProduct = this.products.find((p) => p.id === productData.id);

		if (!existingProduct) {
			this.products.push(productData);
			this.saveToStorage();
			this.updateDisplay();
			this.updateCheckboxes();
		} else {
			console.log('⚠️ Produit déjà présent dans la liste');
		}

		return true;
	}

	/**
	 * Supprime un produit de la comparaison
	 */
	removeProduct(productId) {
		const beforeCount = this.products.length;
		// Convertir productId en number pour assurer la correspondance de type
		const productIdNumber = parseInt(productId, 10);
		this.products = this.products.filter((p) => p.id !== productIdNumber);

		this.saveToStorage();
		this.updateDisplay();
		this.updateCheckboxes();

		// Dispatch l'événement update-comparator pour mettre à jour les sections
		document.dispatchEvent(
			new CustomEvent('update-comparator', {
				detail: {
					selectedProducts: this.products,
					source: 'global',
				},
			}),
		);
	}

	/**
	 * Vide tous les produits
	 */
	clearAll() {
		this.products = [];
		this.saveToStorage();
		this.updateDisplay();
		this.updateCheckboxes();

		// Réajuster le sélecteur de langue
		this.adjustLanguageSelector(false);
	}

	/**
	 * Met à jour l'affichage des barres de comparaison
	 */
	updateDisplay(open = true) {
		const hasProducts = this.products.length > 0;
		const isMobile = this.isMobile();

		// Vérifier si les barres existent (pas sur la page comparateur)
		if (!this.comparisonBar || !this.mobileComparisonBar) {
			return;
		}

		if (isMobile) {
			this.comparisonBar.classList.toggle('comparison-bar--visible', hasProducts);
			this.mobileComparisonBar.classList.toggle('comparison-bar--visible', hasProducts);
		}

		// À l'ajout d'un produit, comportement différent selon l'écran
		if (hasProducts && this.products.length === 1 && open === true) {
			if (isMobile) {
				// La barre mobile reste fermée, l'utilisateur doit cliquer pour l'ouvrir
				console.log('📱 Mobile détecté : barre desktop non ouverte automatiquement');
			} else {
				this.comparisonBar.classList.toggle('comparison-bar--visible', hasProducts);
				this.mobileComparisonBar.classList.toggle('comparison-bar--visible', hasProducts);
				this.openDesktopBar();
			}
		}

		// Mise à jour du contenu desktop
		const desktopProductsContainer = this.comparisonBar.querySelector(
			'[data-comparison-products-desktop]',
		);
		if (desktopProductsContainer) {
			desktopProductsContainer.innerHTML = this.renderProducts();
			desktopProductsContainer.setAttribute('data-product-count', this.products.length);
		}

		// Mise à jour du compteur desktop
		const desktopCountElement = this.comparisonBar.querySelector('[data-comparison-count-desktop]');
		if (desktopCountElement) {
			desktopCountElement.textContent = this.products.length;
		}

		// Mise à jour du contenu mobile
		const mobileProductsContainer = this.mobileComparisonBar.querySelector(
			'[data-comparison-products-mobile]',
		);
		if (mobileProductsContainer) {
			mobileProductsContainer.innerHTML = this.renderProducts();
			mobileProductsContainer.setAttribute('data-product-count', this.products.length);
		}

		// Mise à jour du compteur mobile
		const countElement = this.mobileComparisonBar.querySelector('[data-comparison-count-mobile]');
		if (countElement) {
			countElement.textContent = this.products.length;
		}

		// Réinitialiser le slider quand les produits changent
		this.resetSlider();

		// Recalculer après un court délai pour s'assurer que le DOM est mis à jour
		setTimeout(() => {
			this.updateSliderButtons();
		}, 50);

		// Ajuster la position du sélecteur de langue
		this.adjustLanguageSelector(hasProducts);
	}

	/**
	 * Génère le HTML des produits dans la barre en utilisant le template
	 */
	renderProducts() {
		const template = document.querySelector('[data-comparison-product-template]');
		if (!template) {
			console.warn('⚠️ Template de produit non trouvé');
			return '';
		}

		return this.products
			.map((product) => {
				// Cloner le template
				const productElement = template.content.cloneNode(true);
				const productLink = productElement.querySelector('[data-comparison-product]');

				// Construire l'URL du produit
				let productUrl = '#';
				if (product.url) {
					productUrl = product.url;
				} else if (product.handle) {
					productUrl = `/products/${product.handle}`;
				}

				// Configurer le lien
				if (productUrl !== '#') {
					productLink.href = productUrl;
					productLink.target = '_blank';
				}

				// Remplir les données
				const image = productElement.querySelector('[data-product-image]');
				const title = productElement.querySelector('[data-product-title]');
				const price = productElement.querySelector('[data-product-price]');
				const removeButton = productElement.querySelector('[data-comparison-remove]');

				if (image) {
					image.src = product.image;
					image.alt = product.title;
				}
				if (title) title.textContent = cleanProductTitleGlobal(product.title);
				if (price) price.textContent = product.price;
				if (removeButton) removeButton.dataset.productId = product.id;

				// Retourner le HTML
				const div = document.createElement('div');
				div.appendChild(productElement);
				return div.innerHTML;
			})
			.join('');
	}

	/**
	 * Met à jour les checkboxes sur la page
	 */
	updateCheckboxes() {
		const checkboxes = document.querySelectorAll('.product-compare-checkbox');
		const productIds = this.products.map((p) => p.id);

		checkboxes.forEach((checkbox) => {
			const productData = JSON.parse(checkbox.dataset.productData);
			checkbox.checked = productIds.includes(productData.id);

			// Désactiver si limite atteinte et produit pas sélectionné
			if (this.products.length >= this.maxProducts && !checkbox.checked) {
				checkbox.disabled = true;
				checkbox.parentElement.classList.add('product-compare--disabled');
			} else {
				checkbox.disabled = false;
				checkbox.parentElement.classList.remove('product-compare--disabled');
			}
		});
	}

	/**
	 * Ouvre/ferme la barre mobile
	 */
	toggleMobileBar() {
		// Ne rien faire si on est sur la page du comparateur
		if (this.isComparatorPage || !this.mobileComparisonBar) {
			return;
		}

		const content = this.mobileComparisonBar.querySelector('.comparison-bar__content--mobile');
		const toggle = this.mobileComparisonBar.querySelector('[data-comparison-toggle-mobile]');

		content.classList.toggle('comparison-bar__content--open');
		toggle.classList.toggle('comparison-bar__toggle--open');
	}

	/**
	 * Ferme la barre mobile
	 */
	closeMobileBar() {
		if (!this.mobileComparisonBar) {
			return;
		}

		const content = this.mobileComparisonBar.querySelector('.comparison-bar__content--mobile');
		const toggle = this.mobileComparisonBar.querySelector('[data-comparison-toggle-mobile]');

		content.classList.remove('comparison-bar__content--open');
		toggle.classList.remove('comparison-bar__toggle--open');
	}

	/**
	 * Toggle la barre desktop (masquer/afficher)
	 */
	toggleDesktopBar() {
		if (!this.comparisonBar) {
			return;
		}

		const isOpen = this.comparisonBar.classList.contains('comparison-bar--open');

		if (isOpen) {
			this.minimizeDesktopBar();
		} else {
			this.openDesktopBar();
		}
	}

	/**
	 * Masque la barre desktop (affiche seulement le trigger)
	 */
	minimizeDesktopBar() {
		if (!this.comparisonBar) {
			return;
		}

		this.comparisonBar.classList.remove('comparison-bar--open');
		this.comparisonBar.classList.add('comparison-bar--minimized');
		const toggle = this.comparisonBar.querySelector('[data-comparison-toggle-desktop]');
		if (toggle) {
			toggle.classList.add('comparison-bar__toggle-desktop--minimized');
			// Inverser la flèche
			const svg = toggle.querySelector('svg path');
			if (svg) {
				svg.setAttribute('d', 'M3 6L8 11L13 6');
			}
		}

		// Supprimer l'état ouvert du localStorage (état fermé par défaut)
		localStorage.removeItem('comparison_bar_open');

		// Réajuster le sélecteur de langue quand la barre est fermée
		this.adjustLanguageSelector(this.products.length > 0);
	}

	/**
	 * Affiche la barre desktop complète
	 */
	openDesktopBar() {
		if (!this.comparisonBar) {
			return;
		}

		this.comparisonBar.classList.add('comparison-bar--open');
		this.comparisonBar.classList.remove('comparison-bar--minimized');
		const toggle = this.comparisonBar.querySelector('[data-comparison-toggle-desktop]');
		if (toggle) {
			toggle.classList.remove('comparison-bar__toggle-desktop--minimized');
			// Remettre la flèche vers le haut
			const svg = toggle.querySelector('svg path');
			if (svg) {
				svg.setAttribute('d', 'M3 10L8 5L13 10');
			}
		}

		// Sauvegarder l'état ouvert dans localStorage
		localStorage.setItem('comparison_bar_open', 'true');

		// Réajuster le sélecteur de langue quand la barre est ouverte
		this.adjustLanguageSelector(this.products.length > 0);
	}

	/**
	 * Redirige vers la page de comparaison
	 */
	goToComparison() {
		if (this.products.length < 2) {
			const title = window.languages?.popup_no_schema_title || 'Sélection insuffisante';
			const message =
				window.languages?.min_products_error ||
				'Sélectionnez au moins 2 produits pour les comparer.';

			if (window.ComparisonPopup) {
				window.ComparisonPopup.show(title, message);
			} else {
				alert(message);
			}
			return;
		}

		// Récupérer l'URL de la page de comparaison depuis les settings du thème
		const comparisonPageUrl =
			window.themeSettings?.product_comparison_page_url || '/pages/page-comparator';

		// S'assurer que les produits sont sauvegardés avant redirection
		this.saveToStorage();

		// Rediriger vers la page
		window.location.href = comparisonPageUrl;
	}

	/**
	 * Créer une signature de schéma comme dans search-bar.js
	 * Format: "nombre_de_clés:clé1,clé2,clé3"
	 */
	getSchemaSignature(schema) {
		if (!schema || typeof schema !== 'object') return null;
		try {
			const keys = Object.keys(schema).sort();
			return `${keys.length}:${keys.join(',')}`;
		} catch (e) {
			console.error('Erreur lors de la création de la signature du schéma:', e);
			return null;
		}
	}

	/**
	 * Vérifie la compatibilité du schéma d'un produit avec les produits déjà sélectionnés
	 */
	async checkSchemaCompatibility(newProductData) {
		try {
			const newProductSchema = await this.getProductSchema(newProductData.id);

			if (!newProductSchema) {
				console.warn(`❌ Aucun schéma trouvé pour le nouveau produit ${newProductData.id}`);
				const title = window.languages?.popup_no_schema_title || 'Produit non comparable';
				const message =
					window.languages?.popup_no_schema_message ||
					"Ce produit n'a pas de données de comparaison.";

				if (window.ComparisonPopup) {
					window.ComparisonPopup.show(title, message);
				} else {
					alert(message);
				}
				return false;
			}

			// Récupérer le schéma du premier produit sélectionné comme référence
			const referenceProduct = this.products[0];
			const referenceProductSchema = await this.getProductSchema(referenceProduct.id);

			if (!referenceProductSchema) {
				console.warn(
					`❌ Impossible de récupérer le schéma de référence pour ${referenceProduct.id}`,
				);
				console.log(`⚠️ Autorisation par défaut (pas de vérification possible)`);
				return true; // Laisser passer si on ne peut pas vérifier
			}

			// Comparer les schémas
			const newSchemaKeys = Object.keys(newProductSchema).sort();
			const referenceSchemaKeys = Object.keys(referenceProductSchema).sort();

			// Vérifier le nombre de caractéristiques
			if (newSchemaKeys.length !== referenceSchemaKeys.length) {
				console.warn(`❌ INCOMPATIBLE: Nombre de caractéristiques différent`);
				console.warn(
					`   Nouveau: ${newSchemaKeys.length} | Référence: ${referenceSchemaKeys.length}`,
				);
				const title =
					window.languages?.popup_different_characteristics_title ||
					'Nombre de caractéristiques différent';
				const message =
					window.languages?.popup_different_characteristics_message
						?.replace('{{ new_count }}', newSchemaKeys.length)
						?.replace('{{ new_plural }}', newSchemaKeys.length > 1 ? 's' : '')
						?.replace('{{ ref_count }}', referenceSchemaKeys.length) ||
					`Ce produit a ${newSchemaKeys.length} caractéristique${newSchemaKeys.length > 1 ? 's' : ''} alors que les produits sélectionnés en ont ${referenceSchemaKeys.length}. Les produits doivent avoir le même nombre de caractéristiques.`;

				if (window.ComparisonPopup) {
					window.ComparisonPopup.show(title, message);
				} else {
					alert(message);
				}
				return false;
			}

			// Vérifier que les clés sont identiques
			const keysMatch = newSchemaKeys.every((key, index) => {
				const match = key === referenceSchemaKeys[index];
				return match;
			});

			if (!keysMatch) {
				console.warn(`❌ INCOMPATIBLE: Caractéristiques différentes`);
				console.warn(`   Nouveau:`, newSchemaKeys);
				console.warn(`   Référence:`, referenceSchemaKeys);

				// Trouver les différences
				const missingInNew = referenceSchemaKeys.filter((key) => !newSchemaKeys.includes(key));
				const extraInNew = newSchemaKeys.filter((key) => !referenceSchemaKeys.includes(key));

				if (missingInNew.length > 0) {
					console.warn(`   Manquant dans le nouveau:`, missingInNew);
				}
				if (extraInNew.length > 0) {
					console.warn(`   En plus dans le nouveau:`, extraInNew);
				}

				const title = window.languages?.popup_incompatible_schema_title || 'Produits incompatibles';
				const message =
					window.languages?.popup_incompatible_schema_message ||
					'Ce produit ne peut pas être comparé avec les produits sélectionnés (caractéristiques différentes).';

				if (window.ComparisonPopup) {
					window.ComparisonPopup.show(title, message);
				} else {
					alert(message);
				}
				return false;
			}

			return true;
		} catch (error) {
			console.error('💥 Erreur lors de la vérification de compatibilité des schémas:', error);
			return true; // Laisser passer en cas d'erreur
		}
	}

	/**
	 * Initialise le slider pour la barre de comparaison
	 */
	initSlider() {
		this.currentSlideIndex = 0;
		this.slidesPerView = 3; // Valeur par défaut, sera recalculée dynamiquement
		this.slideStep = 1; // Nombre de produits à faire défiler à chaque clic

		// Attendre que les éléments soient rendus avant de calculer les dimensions
		setTimeout(() => {
			this.updateSliderButtons();
		}, 100);
	}

	/**
	 * Met à jour l'état des boutons de navigation du slider
	 */
	updateSliderButtons() {
		if (!this.comparisonBar) return;

		const totalSlides = this.products.length;

		// Recalculer le nombre de produits visibles pour desktop
		const desktopScroller = this.comparisonBar.querySelector('.comparison-bar__scroller');
		const desktopProducts = this.comparisonBar.querySelectorAll('.comparison-bar__product');

		if (desktopScroller && desktopProducts.length > 0) {
			const scrollerWidth = desktopScroller.offsetWidth;
			const productWidth = desktopProducts[0].offsetWidth;
			const gap = 15;
			const itemWidth = productWidth + gap;
			const visibleProducts = Math.floor(scrollerWidth / itemWidth);
			this.slidesPerView = Math.max(1, visibleProducts);
		}

		const canScrollPrev = this.currentSlideIndex > 0;
		const maxSlideIndex = Math.max(0, totalSlides - this.slidesPerView);
		const canScrollNext = this.currentSlideIndex < maxSlideIndex;

		// Boutons desktop seulement
		const desktopPrevBtn = this.comparisonBar.querySelector('[data-comparison-scroll-prev]');
		const desktopNextBtn = this.comparisonBar.querySelector('[data-comparison-scroll-next]');

		if (desktopPrevBtn) {
			desktopPrevBtn.disabled = !canScrollPrev;
		}
		if (desktopNextBtn) {
			desktopNextBtn.disabled = !canScrollNext;
		}

		// Masquer les boutons si pas assez de produits (desktop seulement)
		const showButtons = totalSlides > this.slidesPerView;
		this.toggleSliderButtons(showButtons);
	}

	/**
	 * Affiche ou masque les boutons de navigation
	 */
	toggleSliderButtons(show) {
		const desktopButtons = this.comparisonBar?.querySelectorAll('.comparison-bar__scroll-button');

		desktopButtons?.forEach((button) => {
			if (button) {
				button.style.display = show ? 'flex' : 'none';
			}
		});
	}

	/**
	 * Fait défiler le slider dans la direction spécifiée
	 */
	scrollSlider(direction, isMobile = false) {
		// Pas de slider en mobile - seulement scroll vertical
		if (isMobile) return;

		const totalSlides = this.products.length;

		// Obtenir les éléments du DOM
		const targetBar = this.comparisonBar;
		const scrollerInner = targetBar?.querySelector('.comparison-bar__scroller-inner');
		const scroller = targetBar?.querySelector('.comparison-bar__scroller');

		if (!scrollerInner || !scroller) return;

		// Calculer les dimensions réelles
		const scrollerWidth = scroller.offsetWidth;
		const products = scrollerInner.querySelectorAll('.comparison-bar__product');

		if (products.length === 0) return;

		const productWidth = products[0].offsetWidth;
		const gap = 15;
		const itemWidth = productWidth + gap;

		// Calculer combien de produits peuvent être visibles
		const visibleProducts = Math.floor(scrollerWidth / itemWidth);
		this.slidesPerView = Math.max(1, visibleProducts);

		// Calculer l'index maximum
		const maxSlideIndex = Math.max(0, totalSlides - this.slidesPerView);

		if (direction === 'prev' && this.currentSlideIndex > 0) {
			this.currentSlideIndex = Math.max(0, this.currentSlideIndex - this.slideStep);
		} else if (direction === 'next' && this.currentSlideIndex < maxSlideIndex) {
			this.currentSlideIndex = Math.min(maxSlideIndex, this.currentSlideIndex + this.slideStep);
		}

		// Appliquer la transformation
		const translateX = -(this.currentSlideIndex * itemWidth);
		scrollerInner.style.transform = `translateX(${translateX}px)`;

		this.updateSliderButtons();
	}

	/**
	 * Réinitialise le slider quand les produits changent
	 */
	resetSlider() {
		this.currentSlideIndex = 0;

		// Remettre à zéro la position (desktop seulement)
		const desktopScrollerInner = this.comparisonBar?.querySelector(
			'.comparison-bar__scroller-inner',
		);

		if (desktopScrollerInner) {
			desktopScrollerInner.style.transform = 'translateX(0px)';
		}

		this.updateSliderButtons();
	}

	/**
	 * Ajuste la position du sélecteur de langue pour éviter qu'il soit caché par la barre de comparaison
	 */
	adjustLanguageSelector(hasProducts) {
		// Fonction pour trouver le shadow root et ajuster le style
		const adjustInShadowRoot = () => {
			// Chercher les éléments qui pourraient contenir le shadow root
			const possibleHosts = [
				'ls-sw-languages',
				'[data-ls-sw-languages]',
				'[class*="language"]',
				'[class*="ls-sw"]',
			];

			for (const selector of possibleHosts) {
				const host = document.querySelector(selector);
				if (host && host.shadowRoot) {
					const languageSelector = host.shadowRoot.querySelector('.ls-sw-languages');
					if (languageSelector) {
						if (hasProducts) {
							languageSelector.style.bottom = '120px';
						} else {
							languageSelector.style.bottom = '60px';
						}
						return true;
					}
				}
			}

			// Si pas trouvé par sélecteur, chercher tous les éléments avec shadow root
			const allElements = document.querySelectorAll('*');
			for (const element of allElements) {
				if (element.shadowRoot) {
					const languageSelector = element.shadowRoot.querySelector('.ls-sw-languages');
					if (languageSelector) {
						if (hasProducts) {
							languageSelector.style.bottom = '120px';
						} else {
							languageSelector.style.bottom = '60px';
						}
						return true;
					}
				}
			}

			return false;
		};

		// Essayer d'ajuster immédiatement
		if (!adjustInShadowRoot()) {
			// Si pas trouvé, essayer après un délai
			setTimeout(() => {
				adjustInShadowRoot();
			}, 100);
		}
	}

	/**
	 * Récupère le schéma d'un produit via l'API de comparaison
	 */
	async getProductSchema(productId) {
		try {
			// Fallback prioritaire : chercher dans les éléments DOM existants
			const checkbox = document.querySelector(`#compare-${productId}`);

			if (checkbox) {
				// Nouvelle méthode : chercher le script JSON dans le conteneur parent
				const parentContainer = checkbox.closest('.product-compare');

				if (parentContainer) {
					const schemaScript = parentContainer.querySelector('.product-schema-data');

					if (schemaScript) {
						try {
							const rawSchemaText = schemaScript.textContent || schemaScript.innerText;
							const schema = JSON.parse(rawSchemaText);
							return schema;
						} catch (e) {
							console.error('❌ Erreur parsing schema depuis script JSON:', e);
						}
					}

					// Fallback : chercher l'ancien attribut data-schema si le script n'existe pas
					const legacySchemaAttr = parentContainer.getAttribute('data-schema');
					if (legacySchemaAttr) {
						console.log(`🔄 Fallback vers data-schema:`, legacySchemaAttr);
						try {
							const schema = JSON.parse(legacySchemaAttr);
							console.log(`📊 Schéma parsé depuis data-schema:`, schema);
							return schema;
						} catch (e) {
							console.error('❌ Erreur parsing data-schema fallback:', e);
						}
					}
				} else {
					console.warn(`❌ Pas de conteneur .product-compare trouvé pour le produit ${productId}`);
				}
			} else {
				console.warn(`❌ Checkbox #compare-${productId} non trouvée`);
				// Chercher toutes les checkboxes de comparaison pour debug
				const allCheckboxes = document.querySelectorAll('.product-compare-checkbox');
				console.log(
					`🔍 Toutes les checkboxes trouvées (${allCheckboxes.length}):`,
					Array.from(allCheckboxes).map((cb) => cb.id),
				);
			}

			// Utiliser l'API de comparaison existante si disponible
			if (window.productComparisonAPI) {
				console.log(`🔧 Méthodes disponibles:`, Object.keys(window.productComparisonAPI));
				console.log(
					`📞 getProductMetafield disponible:`,
					typeof window.productComparisonAPI.getProductMetafield,
				);
			}

			if (
				window.productComparisonAPI &&
				typeof window.productComparisonAPI.getProductMetafield === 'function'
			) {
				console.log(`🔄 Tentative via l'API productComparisonAPI pour le produit ${productId}`);
				try {
					const metafieldValue = await window.productComparisonAPI.getProductMetafield(
						productId,
						'custom',
						'schema',
					);
					console.log(`📡 Réponse de l'API:`, metafieldValue);

					if (metafieldValue) {
						console.log(`✅ Schéma récupéré via API pour le produit ${productId}`);
						if (typeof metafieldValue === 'string') {
							const parsedSchema = JSON.parse(metafieldValue);
							console.log(`📊 Schéma parsé depuis API:`, parsedSchema);
							return parsedSchema;
						} else if (typeof metafieldValue === 'object') {
							console.log(`📊 Schéma objet depuis API:`, metafieldValue);
							return metafieldValue;
						}
					} else {
						console.warn(`❌ API a retourné une valeur vide pour le produit ${productId}`);
					}
				} catch (apiError) {
					console.error(`❌ Erreur lors de l'appel API:`, apiError);
				}
			}

			console.warn(`❌ Aucun schéma trouvé pour le produit ${productId}`);
			return null;
		} catch (error) {
			console.error('💥 Erreur lors de la récupération du schéma du produit:', error);
			return null;
		}
	}

	/**
	 * Sauvegarde les produits dans localStorage
	 */
	saveToStorage() {
		try {
			const jsonString = JSON.stringify(this.products);
			localStorage.setItem(this.storageKey, jsonString);
			console.log('💾 Sauvegarde localStorage:', this.products.length, 'produits');
		} catch (e) {
			console.error('❌ Erreur sauvegarde localStorage:', e);
		}
	}

	/**
	 * Charge les produits depuis le localStorage
	 */
	loadFromStorage() {
		try {
			const stored = localStorage.getItem(this.storageKey);
			if (stored) {
				try {
					const parsedProducts = JSON.parse(stored);
					this.products = parsedProducts;
					console.log('📖 Chargement localStorage:', this.products.length, 'produits');
					return this.products;
				} catch (parseError) {
					console.error('❌ Erreur parsing localStorage:', parseError);
					this.products = [];
				}
			} else {
				this.products = [];
			}
		} catch (e) {
			console.error('❌ Erreur chargement localStorage:', e);
			this.products = [];
		}
		return this.products;
	}

	/**
	 * Restaure l'état de la barre de comparaison depuis localStorage
	 */
	restoreBarState() {
		// Vérifier si la barre existe (pas sur la page comparateur)
		if (!this.comparisonBar) {
			return;
		}

		// Par défaut la barre est fermée, on l'ouvre seulement si elle était explicitement ouverte
		const wasOpen = localStorage.getItem('comparison_bar_open') === 'true';

		// Appliquer l'état immédiatement sans transition
		if (wasOpen) {
			this.openDesktopBar();
		} else {
			// S'assurer qu'elle est bien fermée par défaut
			this.minimizeDesktopBar();
		}

		// Activer les transitions après avoir défini l'état initial
		setTimeout(() => {
			this.comparisonBar.classList.add('comparison-bar--initialized');
		}, 10);
	}

	/**
	 * Récupère les produits sélectionnés
	 */
	static getSelectedProducts() {
		try {
			const stored = localStorage.getItem('eps_comparison_products');
			return stored ? JSON.parse(stored) : [];
		} catch (e) {
			console.warn('Impossible de récupérer les produits sélectionnés:', e);
			return [];
		}
	}

	/**
	 * Nettoie les produits sélectionnés
	 */
	static clearSelectedProducts() {
		try {
			localStorage.removeItem('eps_comparison_products');
		} catch (e) {
			console.warn('Impossible de nettoyer les produits sélectionnés:', e);
		}
	}
}

// Initialiser le système de comparaison au chargement du DOM
document.addEventListener('DOMContentLoaded', () => {
	window.productComparison = new ProductComparison();
});

// Export pour utilisation
if (typeof module !== 'undefined' && module.exports) {
	module.exports = ProductComparison;
}
